/**
 * Confirm-only modal — no reason field, no free text. For destructive
 * actions where provenance lives in an audit event that doesn't need an
 * operator-supplied justification (e.g. deleting a draft payroll run —
 * drafts are disposable by design, the audit trail just records who +
 * when + what was there).
 *
 * Distinct from ReasonModal (which enforces a 1-500 char reason for
 * actions like approve + void that legally or operationally require
 * justification).
 *
 * Headless styling for now. Promote to a Radix Dialog if we grow a
 * third modal flavor.
 */
import { Alert } from './Alert.js';

export function ConfirmDialog(props: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'danger';
  isSubmitting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element | null {
  const {
    open,
    title,
    description,
    confirmLabel,
    confirmVariant,
    isSubmitting,
    errorMessage,
    onCancel,
    onConfirm,
  } = props;
  if (!open) return null;

  const confirmClasses =
    confirmVariant === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-500'
      : 'bg-white text-black hover:bg-gray-200';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
    >
      <div className="w-full max-w-md rounded border border-gray-800 bg-gray-950 p-6 space-y-4">
        <header className="space-y-1">
          <h2 id="confirm-dialog-title" className="font-serif text-xl text-white">
            {title}
          </h2>
          <p className="text-sm text-gray-400">{description}</p>
        </header>

        {errorMessage ? <Alert variant="error">{errorMessage}</Alert> : null}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`rounded px-4 py-2 text-sm font-medium disabled:opacity-50 ${confirmClasses}`}
          >
            {isSubmitting ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
